import { useCallback, useEffect, useState } from "react";
import { Search, Loader2, ChevronDown, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, StatusPill, fmtDateTime, EmptyState } from "./_shared";

const LIMIT = 25;
const STATUS = ["", "new", "in_progress", "resolved", "archived"];
const TOPICS = [
  "",
  "general",
  "assessment",
  "subscription",
  "jobs",
  "government_support",
  "privacy",
  "feedback",
  "other",
];
const NEXT_STATUS_OPTIONS = ["new", "in_progress", "resolved", "archived"];

export default function AdminContact() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [topic, setTopic] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact", {
        params: {
          q: q || undefined,
          status: status || undefined,
          topic: topic || undefined,
          skip,
          limit: LIMIT,
        },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [q, status, topic, skip]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggle = (id) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const updateStatus = async (id, newStatus) => {
    try {
      await api.post(`/admin/contact/${id}/status`, { status: newStatus });
      setData((d) => ({
        ...d,
        items: d.items.map((it) =>
          it.submission_id === id ? { ...it, status: newStatus } : it,
        ),
      }));
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update.");
    }
  };

  /** Open the admin's mail client pre-filled with a friendly reply
   * scaffold + a quoted excerpt of the original message. Auto-bumps the
   * row to `in_progress` if it's currently `new`. */
  const replyViaEmail = (m) => {
    const firstName = (m.name || "").split(" ")[0] || "there";
    const ref = m.submission_id || "—";
    const topicLabel = (m.topic || "general").replace("_", " ");
    const quote = (m.message || "")
      .split("\n")
      .map((line) => `> ${line}`)
      .join("\n");
    const subject = `Re: your message to The AI Took My Job (ref ${ref})`;
    const body = [
      `Hi ${firstName},`,
      "",
      `Thanks for reaching out about ${topicLabel} — happy to help.`,
      "",
      "[ Your reply here ]",
      "",
      "Cheers,",
      "The AI Took My Job team",
      "",
      "—",
      `Your original message (ref ${ref}):`,
      quote,
    ].join("\n");
    const url = `mailto:${encodeURIComponent(m.email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_self");
    // Auto-flip new → in_progress so the inbox reflects we're on it.
    if (m.status === "new") {
      updateStatus(m.submission_id, "in_progress");
    }
  };

  return (
    <AdminLayout
      title="Contact inbox"
      subtitle="Messages submitted via the public Contact page."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-contact-filters">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
              <input
                value={q}
                onChange={(e) => {
                  setSkip(0);
                  setQ(e.target.value);
                }}
                placeholder="Search by name, email or message..."
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid="admin-contact-search-input"
              />
            </div>
            <select
              value={status}
              onChange={(e) => {
                setSkip(0);
                setStatus(e.target.value);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid="admin-contact-status-select"
            >
              {STATUS.map((s) => (
                <option key={s} value={s}>
                  {s ? `Status: ${s.replace("_", " ")}` : "All statuses"}
                </option>
              ))}
            </select>
            <select
              value={topic}
              onChange={(e) => {
                setSkip(0);
                setTopic(e.target.value);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid="admin-contact-topic-select"
            >
              {TOPICS.map((t) => (
                <option key={t} value={t}>
                  {t ? `Topic: ${t.replace("_", " ")}` : "All topics"}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card className="overflow-hidden" testid="admin-contact-card">
          {loading ? (
            <div
              className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500"
              data-testid="admin-contact-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="Inbox is empty"
              body="When someone submits the public contact form it'll appear here."
              testid="admin-contact-empty"
            />
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="admin-contact-list">
              {data.items.map((m) => {
                const isOpen = expanded.has(m.submission_id);
                return (
                  <li
                    key={m.submission_id}
                    className="px-5 py-4"
                    data-testid={`admin-contact-row-${m.submission_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-900 truncate">
                            {m.name}
                          </span>
                          <span className="text-xs text-slate-500 truncate">{m.email}</span>
                          <StatusPill
                            value={m.topic.replace("_", " ")}
                            tone="sky"
                          />
                          <StatusPill
                            value={m.status.replace("_", " ")}
                            tone={
                              m.status === "new"
                                ? "amber"
                                : m.status === "resolved"
                                  ? "green"
                                  : m.status === "archived"
                                    ? "slate"
                                    : "sky"
                            }
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => toggle(m.submission_id)}
                          className="mt-2 text-left text-sm text-slate-600 leading-relaxed"
                          data-testid={`admin-contact-toggle-${m.submission_id}`}
                        >
                          {isOpen ? (
                            <p className="whitespace-pre-wrap">{m.message}</p>
                          ) : (
                            <p className="line-clamp-2">{m.message}</p>
                          )}
                          <span className="inline-flex items-center gap-1 text-xs text-sky-700 mt-1">
                            <ChevronDown
                              className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            />
                            {isOpen ? "Show less" : "Show full message"}
                          </span>
                        </button>
                        <div className="mt-2 text-xs text-slate-400">
                          {m.phone ? `${m.phone} · ` : ""}
                          {fmtDateTime(m.created_at)} · ref{" "}
                          <span className="font-mono">{m.submission_id}</span>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-2">
                        <button
                          type="button"
                          onClick={() => replyViaEmail(m)}
                          disabled={!m.email}
                          title={m.email ? `Reply to ${m.email}` : "No email on this submission"}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40"
                          data-testid={`admin-contact-reply-${m.submission_id}`}
                        >
                          <Mail className="h-3 w-3" /> Reply via email
                        </button>
                        <select
                          value={m.status}
                          onChange={(e) => updateStatus(m.submission_id, e.target.value)}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
                          data-testid={`admin-contact-status-${m.submission_id}`}
                        >
                          {NEXT_STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s.replace("_", " ")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-contact"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
